// src/components/PickCard.js
//
// Renders one game's pick card: two team buttons separated by VS.
// Used by both PickSubmission (user-facing) and AdminPickEntry (admin tool).
//
// Behavior: handles both real-team games (Round 1) and placeholder games
// (Round 2+ before previous round results are recorded). When placeholder,
// the team buttons show the resolved label from the participant's upstream
// pick, or the matchup if no upstream pick exists.
//
// Read-only mode (readOnly prop, set when game.status === 'final'):
//   - Buttons are disabled and not clickable.
//   - Three visual states based on whether the participant picked, and
//     whether their pick was correct:
//       (a) Correct pick     — picked team has green border + ✓; result
//                              strip shows "Correct — N pts" in green.
//       (b) Wrong pick       — picked team has red border + ✗; winner
//                              gets a "Won" label and green ring; result
//                              strip shows "Wrong — 0 pts" in red.
//       (c) No pick recorded — neither team picked-styled; winner gets
//                              "Won" label and green ring; loser is
//                              dimmed; result strip shows a LOUD red
//                              banner: "No pick recorded — 0 pts".
//   - Read-only mode is only meaningful for real games (final games are
//     never placeholders), so the placeholder branches collapse here.

import React from 'react';
import { getResolvedLabel, resolvePickedTeamId } from '../lib/pickGroups';

function PickCard({ game, round, myPick, picks, gameMap, saving, onPick, readOnly = false, deadlineLocked = false, distribution = null, regionDisplayNames = null }) {
    const isSaving      = saving;
    const isPlaceholder = !game.team1_id || !game.team2_id;
    const isFinal       = game.status === 'final';

    // For real games, the pick may have been saved while the game was still a
    // placeholder (picked_team_id = NULL, source_game_pick_id pointing at the
    // upstream game). resolvePickedTeamId walks the chain to recover the
    // effective team id so the highlight renders correctly. For placeholder
    // games we still match by source_game_pick_id directly because the user
    // is choosing between two upstream games, not between two teams.
    const effectiveTeamId = !isPlaceholder ? resolvePickedTeamId(myPick, picks) : null;

    const picked1 = myPick && (
        isPlaceholder
            ? myPick.source_game_pick_id === game.source_game1_id
            : effectiveTeamId === game.team1_id
    );
    const picked2 = myPick && (
        isPlaceholder
            ? myPick.source_game_pick_id === game.source_game2_id
            : effectiveTeamId === game.team2_id
    );

    const label1 = isPlaceholder
        ? getResolvedLabel(game.source_game1_id, gameMap, picks)
        : game.team1_name;
    const label2 = isPlaceholder
        ? getResolvedLabel(game.source_game2_id, gameMap, picks)
        : game.team2_name;

    // For placeholder games, the header shows the round name (e.g. "Second Round")
    // since regions don't apply yet. For real-team games, show the region --
    // preferring the tournament's real-world region name (e.g. "Albany 1"
    // for a women's tournament, where regions are named by host site
    // rather than direction) over the plain internal East/West/South/
    // Midwest slot name, when one has been saved. Same fallback pattern
    // as AdminSeeding.js and AdminGameResults.js.
    const header = isPlaceholder
        ? round.round_name
        : (regionDisplayNames?.[game.team1_region] || game.team1_region);

    // Final-game result derivation. Only meaningful when readOnly && isFinal
    // (a final game with results recorded). winner_id is set; is_correct may
    // be true / false / null. If myPick is missing entirely, the participant
    // never picked.
    const winnerIsTeam1 = isFinal && game.winner_id === game.team1_id;
    const winnerIsTeam2 = isFinal && game.winner_id === game.team2_id;
    const hasNoPick     = readOnly && isFinal && !myPick;
    const isCorrect     = readOnly && isFinal && myPick && myPick.is_correct === true;
    const isWrong       = readOnly && isFinal && myPick && myPick.is_correct === false;
    const pointsEarned  = (myPick && myPick.points_earned) || 0;

    // Determine the styling category for each team button. Final-game
    // buttons override the normal orange "picked" treatment because we
    // need to convey correct/wrong/winner separately from "this is what
    // you picked".
    function buttonClass(picked, isWinner) {
        const base = 'flex-1 font-bold py-3 px-4 rounded-lg transition-colors text-sm border ';

        if (readOnly && isFinal) {
            // Correct pick: this team is both the winner AND the user's pick
            if (picked && isWinner) {
                return base + 'bg-green-900/40 border-green-500 text-white';
            }
            // Wrong pick: this is the team the user picked, but it lost
            if (picked && !isWinner) {
                return base + 'bg-red-900/40 border-red-500 text-white';
            }
            // Winner that the user did NOT pick (either picked the other team,
            // or didn't pick at all)
            if (!picked && isWinner) {
                return base + 'bg-gray-800 border-green-600 text-white';
            }
            // Loser that the user did NOT pick — fully dimmed
            return base + 'bg-gray-900 border-gray-800 text-gray-500';
        }

        // Deadline-locked (scheduled game, deadline passed, not yet final).
        // Show the saved pick in muted amber so the participant can see
        // what they locked in, but no hover effects and cursor is default.
        if (deadlineLocked) {
            if (picked) {
                return base + 'bg-amber-900/40 border-amber-700 text-amber-200 cursor-default';
            }
            return base + 'bg-gray-900 border-gray-800 text-gray-600 cursor-default';
        }

        // Normal (editable) styling
        if (picked) {
            return base + 'bg-[var(--org-accent,#f97316)] border-[var(--org-accent,#fb923c)] text-white';
        }
        return base + 'bg-gray-800 border-gray-700 text-gray-300 hover:border-[var(--org-accent,#f97316)] hover:text-white';
    }

    // Inert click handler for read-only mode — keeps the same button shape
    // and event surface but does nothing. We still pass the disabled prop
    // for accessibility and to suppress hover effects.
    const handleClick1 = (readOnly || deadlineLocked)
        ? () => {}
        : () => onPick(game.id, isPlaceholder ? game.source_game1_id : game.team1_id, label1, isPlaceholder);
    const handleClick2 = (readOnly || deadlineLocked)
        ? () => {}
        : () => onPick(game.id, isPlaceholder ? game.source_game2_id : game.team2_id, label2, isPlaceholder);

    return (
        <div className="bg-gray-900 border border-gray-800 rounded-xl px-5 py-4">
            {/* LOUD red banner for missed picks on final games */}
            {hasNoPick && (
                <div className="bg-red-900/60 border border-red-600 text-red-200 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-lg mb-3 flex items-center justify-between">
                    <span>No pick recorded</span>
                    <span className="text-red-300">0 pts</span>
                </div>
            )}

            <p className="text-xs text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                <span>{header}</span>
                {isSaving && <span className="text-[var(--org-accent,#fb923c)]">Saving...</span>}
                {readOnly && isFinal && !hasNoPick && (
                    isCorrect ? (
                        <span className="ml-auto text-green-400 font-bold normal-case tracking-normal">
                            Correct — {pointsEarned} pts
                        </span>
                    ) : isWrong ? (
                        <span className="ml-auto text-red-400 font-bold normal-case tracking-normal">
                            Wrong — 0 pts
                        </span>
                    ) : null
                )}
            </p>

            <div className="flex items-center gap-3">
                <button
                    onClick={handleClick1}
                    disabled={isSaving || readOnly || deadlineLocked}
                    className={buttonClass(picked1, winnerIsTeam1)}>
                    {!isPlaceholder && <span className="text-xs opacity-60 mr-1">{game.team1_seed}.</span>}
                    {label1}
                    {picked1 && !readOnly && <span className="ml-2">✓</span>}
                    {readOnly && isFinal && picked1 && isCorrect && <span className="ml-2 text-green-400">✓</span>}
                    {readOnly && isFinal && picked1 && isWrong && <span className="ml-2 text-red-400">✗</span>}
                    {readOnly && isFinal && winnerIsTeam1 && !picked1 && (
                        <span className="ml-2 text-xs text-green-400 font-normal normal-case">Won</span>
                    )}
                </button>
                <span className="text-gray-600 font-bold text-xs">VS</span>
                <button
                    onClick={handleClick2}
                    disabled={isSaving || readOnly || deadlineLocked}
                    className={buttonClass(picked2, winnerIsTeam2)}>
                    {!isPlaceholder && <span className="text-xs opacity-60 mr-1">{game.team2_seed}.</span>}
                    {label2}
                    {picked2 && !readOnly && <span className="ml-2">✓</span>}
                    {readOnly && isFinal && picked2 && isCorrect && <span className="ml-2 text-green-400">✓</span>}
                    {readOnly && isFinal && picked2 && isWrong && <span className="ml-2 text-red-400">✗</span>}
                    {readOnly && isFinal && winnerIsTeam2 && !picked2 && (
                        <span className="ml-2 text-xs text-green-400 font-normal normal-case">Won</span>
                    )}
                </button>
            </div>

            {/* Pick distribution — shown after deadline has passed (deadlineLocked)
                or once the game is final. Only rendered when distribution data is
                available. Percentages are of total pool entries, not just pickers,
                so they sum to ≤100% when some participants haven't picked. */}
            {distribution && (deadlineLocked || (readOnly && isFinal)) && (() => {
                const { team1_count, team2_count, total } = distribution;
                const pct1 = total > 0 ? Math.round((team1_count / total) * 100) : 0;
                const pct2 = total > 0 ? Math.round((team2_count / total) * 100) : 0;
                return (
                    <div className="mt-3 pt-3 border-t border-gray-800">
                        <p className="text-xs text-gray-600 uppercase tracking-wide mb-2">Pool picks</p>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400 w-8 text-right font-bold">{pct1}%</span>
                            <div className="flex-1 flex rounded overflow-hidden h-2 bg-gray-800">
                                {pct1 > 0 && (
                                    <div
                                        className="bg-[var(--org-accent,#f97316)] h-2 transition-all"
                                        style={{ width: `${pct1}%` }}
                                    />
                                )}
                            </div>
                            <div className="flex-1 flex rounded overflow-hidden h-2 bg-gray-800 flex-row-reverse">
                                {pct2 > 0 && (
                                    <div
                                        className="bg-[var(--org-accent,#f97316)] h-2 transition-all"
                                        style={{ width: `${pct2}%` }}
                                    />
                                )}
                            </div>
                            <span className="text-xs text-gray-400 w-8 font-bold">{pct2}%</span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1 text-center">
                            {team1_count} of {total} picked {label1} · {team2_count} of {total} picked {label2}
                        </p>
                    </div>
                );
            })()}
        </div>
    );
}

export default PickCard;